import React, { useState, useEffect, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import RichTextEditor from "../components/RichTextEditor";
import { Plus, Edit2, Trash2, Eye, EyeOff, Search, FileText, HelpCircle, Scale } from "lucide-react";

const RESOURCE_TYPES = [
  { value: "blog", label: "Blog" },
  { value: "guide", label: "Guides" },
  { value: "tutorial", label: "Tutorials" },
];
const LEGAL_SLOTS = [
  { slug: "terms", label: "Terms & Conditions" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "refund-policy", label: "Refund Policy" },
  { slug: "seller-policy", label: "Seller Policy" },
];

/* Confirm modal */
function ConfirmDialog({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="tl-card p-6 max-w-sm w-full mx-4" data-testid="confirm-dialog">
        <h3 className="font-display font-semibold text-lg mb-2">{title}</h3>
        <p className="text-sm text-[#475569] mb-6">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="tl-btn-ghost text-sm">Cancel</button>
          <button onClick={onConfirm} data-testid="confirm-yes" className="tl-btn-primary text-sm !bg-red-500 hover:!bg-red-600">Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main CMS pane ─────────────────────────────────────────────────── */
export function AdminCMSPane() {
  const [sub, setSub] = useState("blog");
  const subs = [
    { id: "blog", label: "Blog", icon: FileText },
    { id: "guide", label: "Guides", icon: FileText },
    { id: "tutorial", label: "Tutorials", icon: FileText },
    { id: "faqs", label: "FAQs", icon: HelpCircle },
    { id: "legal", label: "Legal", icon: Scale },
  ];
  return (
    <div>
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar">
        {subs.map((s) => (
          <button key={s.id} data-testid={`cms-sub-${s.id}`} onClick={() => setSub(s.id)}
            className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm border ${sub === s.id ? "bg-[#2563EB] border-[#2563EB]" : "border-slate-200 text-[#475569] hover:text-slate-900"}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>
      {["blog", "guide", "tutorial"].includes(sub) && <ResourcesTable type={sub} />}
      {sub === "faqs" && <FAQsTable />}
      {sub === "legal" && <LegalTable />}
    </div>
  );
}

/* ─── Resources table ───────────────────────────────────────────────── */
function ResourcesTable({ type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | resource id
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ type });
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/admin/resources?${params.toString()}`).then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(load, [type, statusFilter]);

  const filtered = useMemo(() =>
    items.filter((i) => !q || i.title.toLowerCase().includes(q.toLowerCase()) || (i.category || "").toLowerCase().includes(q.toLowerCase())),
  [items, q]);

  const publish = async (id) => { try { await api.put(`/admin/resources/${id}/publish`); toast.success("Published"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const unpublish = async (id) => { try { await api.put(`/admin/resources/${id}/unpublish`); toast.success("Moved to draft"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const doDelete = async () => { const id = confirmDelete; setConfirmDelete(null); try { await api.delete(`/admin/resources/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (editing) return <ResourceEditor type={type} rid={editing === "new" ? null : editing} onDone={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
          <input data-testid={`cms-${type}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or category…" className="tl-input pl-10 w-full" />
        </div>
        <select data-testid={`cms-${type}-status-filter`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="tl-input sm:max-w-[180px]">
          <option value="all">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
        </select>
        <button data-testid={`cms-${type}-new`} onClick={() => setEditing("new")} className="tl-btn-primary shrink-0"><Plus className="w-4 h-4" /> New {type}</button>
      </div>

      <div className="tl-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200">
              <tr><th className="text-left p-3 tl-label">Title</th><th className="text-left p-3 tl-label">Status</th><th className="text-left p-3 tl-label">Author</th><th className="text-left p-3 tl-label">Published</th><th className="text-left p-3 tl-label">Updated</th><th className="p-3 tl-label">Actions</th></tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="p-8 text-center text-[#475569]">Loading…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-[#475569]">No {type} entries yet</td></tr>
              : filtered.map((r) => (
                <tr key={r.id} data-testid={`cms-row-${r.id}`} className="border-b border-white/[0.04]">
                  <td className="p-3">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-[#64748B]">{r.slug}{r.category ? ` · ${r.category}` : ""}</div>
                  </td>
                  <td className="p-3"><StatusChip status={r.status} /></td>
                  <td className="p-3 text-[#475569]">{r.author || "—"}</td>
                  <td className="p-3 text-[#475569] text-xs">{r.published_at ? new Date(r.published_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3 text-[#475569] text-xs">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button data-testid={`cms-edit-${r.id}`} onClick={() => setEditing(r.id)} title="Edit" className="p-1.5 hover:bg-white/[0.06] rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                      {r.status !== "published"
                        ? <button data-testid={`cms-publish-${r.id}`} onClick={() => publish(r.id)} title="Publish" className="p-1.5 hover:bg-white/[0.06] rounded text-[#0891B2]"><Eye className="w-3.5 h-3.5" /></button>
                        : <button data-testid={`cms-unpublish-${r.id}`} onClick={() => unpublish(r.id)} title="Unpublish" className="p-1.5 hover:bg-white/[0.06] rounded text-[#F59E0B]"><EyeOff className="w-3.5 h-3.5" /></button>}
                      <button data-testid={`cms-delete-${r.id}`} onClick={() => setConfirmDelete(r.id)} title="Delete" className="p-1.5 hover:bg-white/[0.06] rounded text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog open={!!confirmDelete} title="Delete this resource?" message="This can't be undone. Any public link to it will 404." onConfirm={doDelete} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}

function StatusChip({ status }) {
  const c = status === "published" ? "!bg-[#0891B2]/15 !text-[#0891B2]" : status === "scheduled" ? "!bg-[#F59E0B]/15 !text-[#F59E0B]" : "";
  return <span className={`tl-chip ${c}`}>{status}</span>;
}

/* ─── Resource editor ──────────────────────────────────────────────── */
function ResourceEditor({ type, rid, onDone }) {
  const [form, setForm] = useState({
    type, title: "", slug: "", excerpt: "", content: "", featured_image: "",
    author: "", category: "", tags: "", status: "draft", featured: false,
    published_at: "", seo_title: "", seo_description: "", seo_keywords: "",
    social_image: "", reading_minutes: 0, sort_order: 0,
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!rid);

  useEffect(() => {
    if (!rid) return;
    api.get(`/admin/resources/${rid}`).then((r) => {
      const d = r.data;
      // Coerce nulls → '' so React controlled inputs don't warn.
      setForm({
        type: d.type || type,
        title: d.title || "",
        slug: d.slug || "",
        excerpt: d.excerpt || "",
        content: d.content || "",
        featured_image: d.featured_image || "",
        author: d.author || "",
        category: d.category || "",
        tags: (d.tags || []).join(", "),
        status: d.status || "draft",
        featured: !!d.featured,
        published_at: d.published_at ? d.published_at.slice(0, 16) : "",
        seo_title: d.seo_title || "",
        seo_description: d.seo_description || "",
        seo_keywords: (d.seo_keywords || []).join(", "),
        social_image: d.social_image || "",
        reading_minutes: d.reading_minutes || 0,
        sort_order: d.sort_order || 0,
      });
    }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [rid, type]);

  const save = async (nextStatus) => {
    setBusy(true);
    const payload = {
      ...form,
      tags: form.tags ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
      seo_keywords: form.seo_keywords ? form.seo_keywords.split(",").map((s) => s.trim()).filter(Boolean) : [],
      reading_minutes: form.reading_minutes ? Number(form.reading_minutes) : null,
      sort_order: Number(form.sort_order) || 0,
      status: nextStatus || form.status,
      published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
    };
    try {
      if (rid) await api.put(`/admin/resources/${rid}`, payload);
      else await api.post("/admin/resources", payload);
      toast.success(rid ? "Saved" : "Created");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-16 text-[#475569]">Loading…</div>;

  return (
    <div data-testid="cms-editor" className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="font-display font-semibold text-xl">{rid ? "Edit" : "New"} {type}</h2>
        <div className="flex gap-2">
          <button data-testid="cms-cancel" onClick={onDone} className="tl-btn-ghost text-sm">Cancel</button>
          <button data-testid="cms-save-draft" disabled={busy} onClick={() => save("draft")} className="tl-btn-ghost text-sm disabled:opacity-50">Save draft</button>
          <button data-testid="cms-publish" disabled={busy} onClick={() => save("published")} className="tl-btn-primary text-sm disabled:opacity-50">{busy ? "Saving…" : "Publish"}</button>
        </div>
      </div>

      <div className="tl-card p-5 space-y-4">
        <input data-testid="cms-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="tl-input text-lg" />
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="cms-slug" placeholder="URL slug (auto-generated if blank)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="tl-input font-mono text-sm" />
          <input data-testid="cms-category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tl-input" />
        </div>
        <textarea data-testid="cms-excerpt" rows={2} placeholder="Short description / excerpt (max 500)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="tl-input" />
        <div>
          <div className="tl-label mb-2">Content</div>
          <RichTextEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} testId="cms-content" placeholder="Write your article…" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Featured image URL" value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} className="tl-input" />
          <input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="tl-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="tl-input" />
          <input type="number" placeholder="Reading minutes" value={form.reading_minutes} onChange={(e) => setForm({ ...form, reading_minutes: e.target.value })} className="tl-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="tl-label mb-2 block">Publish date (leave empty = now)</label>
            <input data-testid="cms-published-at" type="datetime-local" value={form.published_at} onChange={(e) => setForm({ ...form, published_at: e.target.value })} className="tl-input" />
          </div>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
            <span className="text-sm">Featured / Recommended</span>
          </label>
        </div>
      </div>

      <details className="tl-card p-5">
        <summary className="cursor-pointer font-medium tl-label">SEO & Social</summary>
        <div className="mt-4 space-y-3">
          <input placeholder="SEO title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="tl-input" />
          <textarea rows={2} placeholder="Meta description" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="tl-input" />
          <input placeholder="SEO keywords (comma separated)" value={form.seo_keywords} onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })} className="tl-input" />
          <input placeholder="Social share image URL" value={form.social_image} onChange={(e) => setForm({ ...form, social_image: e.target.value })} className="tl-input" />
        </div>
      </details>
    </div>
  );
}

/* ─── FAQs table ────────────────────────────────────────────────────── */
function FAQsTable() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/admin/faqs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const doDelete = async () => { const id = confirmDelete; setConfirmDelete(null); try { await api.delete(`/admin/faqs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (editing) return <FAQEditor fid={editing === "new" ? null : editing} onDone={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex justify-end mb-4"><button data-testid="cms-faq-new" onClick={() => setEditing("new")} className="tl-btn-primary"><Plus className="w-4 h-4" /> New FAQ</button></div>
      <div className="tl-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200"><tr><th className="text-left p-3 tl-label">Question</th><th className="text-left p-3 tl-label">Category</th><th className="text-left p-3 tl-label">Order</th><th className="text-left p-3 tl-label">Active</th><th className="p-3 tl-label">Actions</th></tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-[#475569]">No FAQs yet</td></tr>
              : items.map((f) => (
                <tr key={f.id} className="border-b border-white/[0.04]">
                  <td className="p-3 max-w-md"><div className="font-medium line-clamp-1">{f.question}</div></td>
                  <td className="p-3 text-[#475569]">{f.category || "—"}</td>
                  <td className="p-3">{f.sort_order}</td>
                  <td className="p-3"><span className={`tl-chip ${f.active ? "!bg-[#0891B2]/15 !text-[#0891B2]" : ""}`}>{f.active ? "active" : "inactive"}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button data-testid={`cms-faq-edit-${f.id}`} onClick={() => setEditing(f.id)} className="p-1.5 hover:bg-white/[0.06] rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`cms-faq-delete-${f.id}`} onClick={() => setConfirmDelete(f.id)} className="p-1.5 hover:bg-white/[0.06] rounded text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog open={!!confirmDelete} title="Delete this FAQ?" message="This can't be undone." onConfirm={doDelete} onCancel={() => setConfirmDelete(null)} />
    </div>
  );
}

function FAQEditor({ fid, onDone }) {
  const [form, setForm] = useState({ question: "", answer: "", category: "General", sort_order: 0, active: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (fid) api.get("/admin/faqs").then((r) => { const f = r.data.find((x) => x.id === fid); if (f) setForm(f); }); }, [fid]);
  const save = async () => {
    setBusy(true);
    try { const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (fid) await api.put(`/admin/faqs/${fid}`, payload); else await api.post("/admin/faqs", payload);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cms-faq-editor" className="space-y-4">
      <div className="flex justify-between items-center"><h2 className="font-display font-semibold text-xl">{fid ? "Edit" : "New"} FAQ</h2><div className="flex gap-2"><button onClick={onDone} className="tl-btn-ghost text-sm">Cancel</button><button data-testid="cms-faq-save" disabled={busy} onClick={save} className="tl-btn-primary text-sm">{busy ? "Saving…" : "Save"}</button></div></div>
      <div className="tl-card p-5 space-y-3">
        <textarea data-testid="cms-faq-question" rows={2} placeholder="Question" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="tl-input" />
        <div>
          <div className="tl-label mb-2">Answer</div>
          <RichTextEditor value={form.answer} onChange={(v) => setForm({ ...form, answer: v })} testId="cms-faq-answer" placeholder="Write the answer…" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="tl-input" />
          <input type="number" placeholder="Display order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="tl-input" />
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /><span className="text-sm">Active</span></label>
        </div>
      </div>
    </div>
  );
}

/* ─── Legal table ───────────────────────────────────────────────────── */
function LegalTable() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/legal").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  if (editing) return <LegalEditor slug={editing} onDone={() => { setEditing(null); load(); }} />;
  return (
    <div>
      <div className="tl-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200"><tr><th className="text-left p-3 tl-label">Title</th><th className="text-left p-3 tl-label">Slug</th><th className="text-left p-3 tl-label">Version</th><th className="text-left p-3 tl-label">Status</th><th className="text-left p-3 tl-label">Updated</th><th className="p-3 tl-label">Actions</th></tr></thead>
            <tbody>
              {LEGAL_SLOTS.map((slot) => {
                const d = items.find((i) => i.slug === slot.slug);
                return (
                  <tr key={slot.slug} className="border-b border-white/[0.04]">
                    <td className="p-3 font-medium">{d?.title || slot.label}</td>
                    <td className="p-3 font-mono text-xs text-[#475569]">{slot.slug}</td>
                    <td className="p-3 text-[#475569]">{d?.version || "—"}</td>
                    <td className="p-3">{d ? <StatusChip status={d.status} /> : <span className="tl-chip">not created</span>}</td>
                    <td className="p-3 text-[#475569] text-xs">{d?.updated_at ? new Date(d.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="p-3"><button data-testid={`cms-legal-edit-${slot.slug}`} onClick={() => setEditing(slot.slug)} className="tl-btn-secondary text-xs"><Edit2 className="w-3 h-3" /> Edit</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LegalEditor({ slug, onDone }) {
  const [form, setForm] = useState({ slug, title: "", summary: "", content: "", version: "1.0", effective_date: "", status: "draft", seo_title: "", seo_description: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/admin/legal/${slug}`).then((r) => {
      const d = r.data;
      setForm({
        slug: d.slug, title: d.title, summary: d.summary || "", content: d.content || "",
        version: d.version || "1.0",
        effective_date: d.effective_date ? d.effective_date.slice(0, 10) : "",
        status: d.status || "draft",
        seo_title: d.seo_title || "", seo_description: d.seo_description || "",
      });
    }).finally(() => setLoading(false));
  }, [slug]);
  const save = async (nextStatus) => {
    setBusy(true);
    try {
      const payload = { ...form, status: nextStatus || form.status,
        effective_date: form.effective_date ? new Date(form.effective_date).toISOString() : null };
      await api.put(`/admin/legal/${slug}`, payload);
      toast.success("Saved"); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (loading) return <div className="text-center py-12 text-[#475569]">Loading…</div>;
  return (
    <div data-testid="cms-legal-editor" className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="font-display font-semibold text-xl">Edit: {form.title}</h2>
        <div className="flex gap-2 items-center">
          <span className="text-xs text-[#475569]">Current: <StatusChip status={form.status} /></span>
          <button onClick={onDone} className="tl-btn-ghost text-sm">Cancel</button>
          <button data-testid="cms-legal-save-draft" disabled={busy} onClick={() => save("draft")} className="tl-btn-ghost text-sm">Save draft</button>
          <button data-testid="cms-legal-publish" disabled={busy} onClick={() => save("published")} className="tl-btn-primary text-sm">{busy ? "Saving…" : "Publish"}</button>
        </div>
      </div>
      <div className="tl-card p-5 space-y-4">
        <input data-testid="cms-legal-title" placeholder="Page title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="tl-input text-lg" />
        <textarea rows={2} placeholder="Optional summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="tl-input" />
        <div>
          <div className="tl-label mb-2">Content</div>
          <RichTextEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} testId="cms-legal-content" placeholder="Write the legal document…" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Version" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="tl-input" />
          <input type="date" placeholder="Effective date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} className="tl-input" />
        </div>
      </div>
      <details className="tl-card p-5">
        <summary className="cursor-pointer font-medium tl-label">SEO</summary>
        <div className="mt-4 space-y-3">
          <input placeholder="SEO title" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="tl-input" />
          <textarea rows={2} placeholder="Meta description" value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="tl-input" />
        </div>
      </details>
    </div>
  );
}
